import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme, fonts } from "@/theme";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/lib/toast";
import { BRAND, HQ_ROWS } from "@/data/modules";

const ADMIN_TOOLS = [
  { id: "affiliates", label: "Affiliate access", hint: "Invite & manage who gets into Rep HQ", icon: "users", href: "/admin/affiliates" },
  { id: "content", label: "Content Hub", hint: "Edit captions, templates & spotlights", icon: "edit-3", href: "/admin/content" },
  { id: "crystals", label: "Crystal School", hint: "Add & edit crystal entries", icon: "hexagon", href: "/admin/crystals" },
  { id: "lab", label: "Content Lab", hint: "Edit experiments & landing text", icon: "zap", href: "/admin/lab" },
  { id: "shop", label: "Shop settings", hint: "Store domain & affiliate dashboard link", icon: "shopping-bag", href: "/admin/shopify" },
];

export default function HQ() {
  const { colors, scheme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const initial = (user?.name?.trim()?.[0] || "R").toUpperCase();

  const row = (r, onClick, testid) => (
    <button key={r.id} data-testid={testid} className="pressable" onClick={onClick} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: 16, background: "transparent", border: "none", borderBottom: `1px solid ${colors.divider}`, cursor: "pointer", textAlign: "left" }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon name={r.icon} size={18} color={colors.brand} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: colors.onSurface, fontSize: 16, fontWeight: 600, fontFamily: fonts.text }}>{r.label}</div>
        <div style={{ color: colors.muted, fontSize: 12, marginTop: 2, fontFamily: fonts.text }}>{r.hint}</div>
      </div>
      <Icon name="chevron-right" size={20} color={colors.muted} />
    </button>
  );

  const listStyle = { background: colors.surfaceSecondary, borderRadius: 20, border: `1px solid ${colors.border}`, overflow: "hidden" };

  return (
    <div className="ritzy-fade">
      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${colors.divider}` }}>
        <div style={{ color: colors.onSurface, fontSize: 28, fontFamily: fonts.display, fontWeight: 700 }}>My HQ</div>
      </div>
      <div style={{ padding: "20px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
          <div style={{ width: 60, height: 60, borderRadius: 30, background: colors.brand, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ color: colors.onBrand, fontSize: 24, fontFamily: fonts.display, fontWeight: 700 }}>{initial}</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: colors.onSurface, fontSize: 20, fontFamily: fonts.display, fontWeight: 700 }}>{user?.name ?? "Your name"}</div>
            <div style={{ color: colors.muted, fontSize: 14, marginTop: 2, fontFamily: fonts.text }}>{user?.email ?? "you@example.com"}</div>
          </div>
        </div>

        <div style={listStyle}>
          {HQ_ROWS.map((r) => row(r, () => toast.show("Coming soon — you'll be able to edit this later."), `hq-row-${r.id}`))}
          <button data-testid="theme-toggle" className="pressable" onClick={toggle} style={{ width: "100%", display: "flex", alignItems: "center", gap: 14, padding: 16, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: colors.brandTertiary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={scheme === "dark" ? "sun" : "moon"} size={18} color={colors.brand} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: colors.onSurface, fontSize: 16, fontWeight: 600, fontFamily: fonts.text }}>Appearance</div>
              <div style={{ color: colors.muted, fontSize: 12, marginTop: 2, fontFamily: fonts.text }}>{scheme === "dark" ? "Dark mode" : "Light mode"} — tap to switch</div>
            </div>
            <Icon name="refresh-cw" size={18} color={colors.muted} />
          </button>
        </div>

        {user?.is_admin ? (
          <>
            <div style={{ color: colors.onSurface, fontSize: 15, fontFamily: fonts.display, fontWeight: 700, margin: "24px 0 10px" }}>Admin tools 🔑</div>
            <div style={listStyle}>{ADMIN_TOOLS.map((r) => row(r, () => navigate(r.href), `admin-tool-${r.id}`))}</div>
          </>
        ) : null}

        <button data-testid="logout-button" className="pressable" onClick={logout} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 24, padding: "16px", borderRadius: 16, border: `1px solid ${colors.border}`, background: colors.surfaceSecondary, cursor: "pointer" }}>
          <Icon name="log-out" size={18} color={colors.error} />
          <span style={{ color: colors.error, fontSize: 16, fontWeight: 700, fontFamily: fonts.text }}>Log out</span>
        </button>

        <div style={{ color: colors.muted, fontSize: 12, textAlign: "center", marginTop: 24, fontFamily: fonts.text }}>{BRAND.name} · {BRAND.tagline}</div>
      </div>
    </div>
  );
}
